// ===== BEGIN FILE: src/components/report-v2/ReportCover.tsx =====
/**
 * Professional cover page for astrology reports
 * With custom ShreemAstro branded background and precisely positioned text overlays
 */

import React, { useState } from 'react';
import { Box, Typography } from '@mui/joy';
import type { ReportData } from './types';
import { FAMOUS_BY_ASCENDANT } from './data/famousPersonalities';

interface ReportCoverProps {
  data: ReportData;
  title?: string;
  subtitle?: string;
}

/** Single circular personality card — shows a placeholder if the image fails to load. */
const PersonalityCard: React.FC<{ imgSrc: string; name: string; note?: string }> = ({ imgSrc, name, note }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const initial = (name || '?').charAt(0).toUpperCase();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '30%' }}>
      {/* Circular image or placeholder */}
      {imgFailed ? (
        <Box
          sx={{
            width: '100%',
            aspectRatio: '1 / 1',
            borderRadius: '50%',
            border: '2px solid rgba(96,41,65,0.35)',
            background: 'rgba(96,41,65,0.10)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: "'Sanchez', serif",
            fontSize: 'clamp(10px, 1.4vw, 24px)',
            color: '#602941',
            fontWeight: 700,
          }}
        >
          {initial}
        </Box>
      ) : (
        <Box
          component="img"
          src={imgSrc}
          alt={name}
          onError={() => setImgFailed(true)}
          sx={{
            width: '100%',
            aspectRatio: '1 / 1',
            borderRadius: '50%',
            objectFit: 'cover',
            border: '2px solid rgba(96,41,65,0.35)',
            display: 'block',
          }}
        />
      )}
      {/* Name */}
      <Box
        component="div"
        sx={{
          fontFamily: "'Sanchez', serif",
          fontSize: 'clamp(6px, 0.65vw, 11.5px)',
          color: '#602941',
          textAlign: 'center',
          mt: '3%',
          lineHeight: 1.3,
          fontWeight: 600,
          wordBreak: 'break-word',
        }}
      >
        {name}
      </Box>
      {/* Optional note */}
      {note && (
        <Box
          component="div"
          sx={{
            fontFamily: "'Sanchez', serif",
            fontSize: 'clamp(5px, 0.5vw, 9px)',
            color: '#602941',
            opacity: 0.65,
            textAlign: 'center',
            mt: '1%',
            lineHeight: 1.2,
          }}
        >
          {note}
        </Box>
      )}
    </Box>
  );
};

export const ReportCover: React.FC<ReportCoverProps> = ({ data, title, subtitle }) => {
  return (
    <>
      {/* Import Sanchez font from Google Fonts */}
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Sanchez:ital@0;1&display=swap');
        `}
      </style>

      <Box
        className="report-cover"
        sx={{
          width: '100%',
          paddingTop: `${(2480 / 1754) * 100}%`, // Aspect ratio: 1754×2480px = 141.4%
          position: 'relative',
          overflow: 'hidden',
          '@media print': {
            paddingTop: 0,
            height: '297mm',    // A4 height
            width: '100%',      // @page :first { margin: 0 } gives full-bleed — no negative margins needed
            overflow: 'hidden',
            pageBreakAfter: 'always',
          }
        }}
      >
        {/* Custom background image */}
        <Box
          component="img"
          src={`${window.location.origin}/Assets/reports/shreemastro-cover-background.png`}
          alt="ShreemAstro Report Cover"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain', // screen: 'contain' to show full image
            '@media print': {
              objectFit: 'cover',  // print: 'cover' fills the full A4 page, no white edges
            },
          }}
        />

        {/* Text overlay container - positioned absolutely over background */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none', // Don't block interactions with background
          }}
        >
          {/* User Name - Percentage-based positioning (left-aligned) */}
          {(() => {
            const nameLen = Math.max((data.birthDetails.name || '').length, 1);
            // The report container is maxWidth: 210mm (~793px). Text area = 47% = ~373px.
            // Cap with px so large viewports don't over-scale the font beyond what fits.
            // fontPx = 600 / nameLen  (600px ≈ text area width / 0.6 char-width-factor)
            // fontVw = 75.4 / nameLen (for small screens where container < 793px)
            // min(vw, px) picks whichever is smaller — correct on all screen sizes.
            const fontPx = Math.round(600 / nameLen);
            const fontVw = (75.4 / nameLen).toFixed(2);
            const nameFontSize = `clamp(10px, min(${fontVw}vw, ${fontPx}px), 48px)`;
            return (
              <Box
                component="div"
                sx={{
                  position: 'absolute',
                  left: '49.2%',
                  top: '57.4%',
                  width: '47%',
                  fontFamily: "'Sanchez', serif",
                  fontSize: nameFontSize,
                  color: '#602941',
                  fontWeight: 400,
                  lineHeight: 1.15,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {data.birthDetails.name}
              </Box>
            );
          })()}

          {/* Birth Details — stacked, Sanchez throughout to match cover typography */}
          <Box
            sx={{
              position: 'absolute',
              left: '49.2%',
              top: '63.5%',
              width: '47%',
            }}
          >
            {([
              { label: 'Date & Birth Time', value: `${data.birthDetails.dateOfBirth}  |  ${data.birthDetails.timeOfBirth}` },
              { label: 'Place',             value: data.birthDetails.location },
            ] as { label: string; value: string }[]).map(({ label, value }, idx) => (
              <Box
                key={idx}
                component="div"
                sx={{ mb: 'clamp(2px, 0.4vw, 7px)' }}
              >
                {/* Label — spaced uppercase Sanchez, same style as "FAMOUS CANCER ASCENDANTS" */}
                <Box
                  component="span"
                  sx={{
                    fontFamily: "'Sanchez', serif",
                    fontSize: 'clamp(5.5px, 0.55vw, 9.5px)',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: '#602941',
                    opacity: 0.75,
                  }}
                >
                  {label}&nbsp;&nbsp;
                </Box>
                {/* Value — Sanchez italic, same typeface as the name above */}
                <Box
                  component="span"
                  sx={{
                    fontFamily: "'Sanchez', serif",
                    fontStyle: 'italic',
                    fontSize: 'clamp(8px, 0.9vw, 15.5px)',
                    color: '#602941',
                    letterSpacing: '0.01em',
                  }}
                >
                  {value}
                </Box>
              </Box>
            ))}
          </Box>

          {/* Famous Personalities Block - Same Ascendant */}
          {(() => {
            const signKey = (data.birthDetails.ascendant || '').toLowerCase();
            const personalities = FAMOUS_BY_ASCENDANT[signKey];
            if (!personalities) return null;
            return (
              <Box
                sx={{
                  position: 'absolute',
                  left: '49.2%',
                  top: '71%',
                  width: '47%',
                  '@media (max-width: 599px)': {
                    top: '74%',
                  },
                  '@media print': {
                    top: '71%',
                  },
                }}
              >
                {/* Label */}
                <Box
                  component="div"
                  sx={{
                    fontFamily: "'Sanchez', serif",
                    fontSize: 'clamp(7px, 0.8vw, 14px)',
                    color: '#602941',
                    opacity: 0.75,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    mb: '4%',
                  }}
                >
                  Famous {data.birthDetails.ascendant} Ascendants
                </Box>

                {/* 3 circular portrait cards in a row */}
                <Box sx={{ display: 'flex', gap: '2%', alignItems: 'flex-start' }}>
                  {personalities.map((person, idx) => {
                    const imgSrc = `/Assets/reports/famous-personalities/${signKey}-${idx + 1}.jpg`;
                    return (
                      <PersonalityCard
                        key={idx}
                        imgSrc={imgSrc}
                        name={person.name}
                        note={person.note}
                      />
                    );
                  })}
                </Box>
              </Box>
            );
          })()}
        </Box>
      </Box>
    </>
  );
};
